// Custom BPMN renderer applying Stekz design application colors to diagram elements.
// Delegates all drawing to the default bpmnRenderer, then overrides fill/stroke via CSS variable references.
import { is } from "bpmn-js/lib/util/ModelUtil";
import type EventBus from "diagram-js/lib/core/EventBus";
import BaseRenderer from "diagram-js/lib/draw/BaseRenderer";
import type {
    Connection,
    ElementLike,
    Shape,
} from "diagram-js/lib/model/Types";

import { HIGH_PRIORITY } from "../../constants";

// Runs before LaneLabelRenderer (HIGH_PRIORITY) and default bpmnRenderer.
const STEKZ_RENDERER_PRIORITY = HIGH_PRIORITY + 1;

const MARKER_LABEL_HIDDEN = "djs-label-hidden";

export type StekzRendererConfig = {
    hideLaneNames: boolean;
};

/**
 * Custom renderer that applies Stekz design tokens to all BPMN elements.
 * Inline styles use CSS variable references (e.g. "var(--text-color)") so
 * that colors automatically follow light/dark mode without re-rendering.
 */
export default class StekzRenderer extends BaseRenderer {
    static $inject = ["eventBus", "bpmnRenderer", "stekzRendererConfig"];

    private readonly bpmnRenderer: BaseRenderer;
    private readonly hideLaneNames: boolean;

    constructor(
        eventBus: EventBus,
        bpmnRenderer: BaseRenderer,
        config: StekzRendererConfig
    ) {
        super(eventBus, STEKZ_RENDERER_PRIORITY);
        this.bpmnRenderer = bpmnRenderer;
        this.hideLaneNames = config.hideLaneNames;

        // Prevent lane label editing via double-click when lane names are hidden.
        if (config.hideLaneNames) {
            eventBus.on(
                "element.dblclick",
                HIGH_PRIORITY,
                (event: Event & { element: ElementLike }) => {
                    if (is(event.element, "bpmn:Lane")) {
                        return false;
                    }
                }
            );
        }
    }

    /** Handle all shapes and connections except label overlays. */
    canRender(element: ElementLike): boolean {
        return !element.labelTarget;
    }

    drawShape(visuals: SVGElement, shape: Shape): SVGElement {
        const result = this.bpmnRenderer.drawShape(visuals, shape);
        this.applyShapeStyles(visuals, shape);
        return result;
    }

    drawConnection(visuals: SVGElement, connection: Connection): SVGElement {
        const result = this.bpmnRenderer.drawConnection(visuals, connection);
        visuals.querySelectorAll<SVGElement>("path, polyline").forEach((el) => {
            el.style.stroke = "var(--text-color)";
        });
        return result;
    }

    getShapePath(shape: Shape): string {
        return this.bpmnRenderer.getShapePath(shape);
    }

    getConnectionPath(connection: Connection): string {
        return this.bpmnRenderer.getConnectionPath(connection);
    }

    private applyShapeStyles(visuals: SVGElement, shape: Shape): void {
        if (is(shape, "bpmn:Participant")) {
            this.styleParticipant(visuals);
        } else if (is(shape, "bpmn:Lane")) {
            this.styleLane(visuals);
        } else {
            this.styleGenericShape(visuals, shape);
        }
    }

    /**
     * Pool header: use the standard content background so pools visually match lanes
     * and other shapes. Only the stroke and label color are themed.
     */
    private styleParticipant(visuals: SVGElement): void {
        visuals.querySelectorAll<SVGRectElement>("rect").forEach((rect) => {
            rect.style.fill = "var(--p-content-background)";
            rect.style.stroke = "var(--text-color)";
        });
        visuals.querySelectorAll<SVGTextElement>("text").forEach((text) => {
            text.style.fill = "var(--text-color)";
        });
    }

    /**
     * Lane: reset fill to content background with full opacity.
     * bpmn-js sets fill-opacity: 0.25 on lane rects, which must be overridden.
     */
    private styleLane(visuals: SVGElement): void {
        if (this.hideLaneNames) {
            visuals.classList.add(MARKER_LABEL_HIDDEN);
        }
        visuals.querySelectorAll<SVGRectElement>("rect").forEach((rect) => {
            rect.style.fill = "var(--p-content-background)";
            rect.style.fillOpacity = "1";
            rect.style.stroke = "var(--text-color)";
        });
    }

    /**
     * Tasks, events, gateways, etc.
     *
     * The first rect/circle/polygon as a direct child is the main bounding shape. It gets
     * the content-background fill (dark-mode-aware) and the outline stroke. All other
     * drawing primitives — icon paths, nested shapes inside <g> markers, secondary circles
     * like the terminate-event inner circle — are icon/marker elements:
     *   - fill:none is preserved (outline-style icons like message envelope, timer hands)
     *   - any other explicit fill is replaced with the outline color (filled icons like
     *     cross, gear, signal triangle)
     *   - white fills are treated as background/hole elements (e.g. gear cutout) and
     *     mapped to the content background instead of the outline color
     *   - explicit strokes are replaced with the outline color
     *
     * User-set colors from the color picker (di.bioc:fill / di.bioc:stroke) are preserved
     * on both the main shape and icon strokes.
     */
    private styleGenericShape(visuals: SVGElement, shape: Shape): void {
        const di = shape.di;
        const userFill = di?.get?.("bioc:fill");
        const userStroke = di?.get?.("bioc:stroke");

        // Main bounding shape: first direct-child rect, circle, or polygon.
        const mainShape = visuals.querySelector<SVGElement>(
            ":scope > rect, :scope > circle, :scope > polygon"
        );
        if (mainShape) {
            if (!userStroke) mainShape.style.stroke = "var(--text-color)";
            if (!userFill) mainShape.style.fill = "var(--p-content-background)";
            // bpmn-js sets fill-opacity < 1 on gateway polygons; reset so hover fills are visible.
            if (mainShape.tagName.toLowerCase() === "polygon") {
                mainShape.style.fillOpacity = "1";
            }
        }

        // Icon and marker elements: every other drawing primitive in the visuals.
        // Classification is persisted as data-stekz-fill / data-stekz-stroke so the
        // hover layer can reliably restore these values without re-deriving them
        // from bpmn-js presentation attributes (which may be absent when bpmn-js
        // used inline styles instead).
        visuals
            .querySelectorAll<SVGElement>(
                "path, circle, polygon, rect, polyline, ellipse"
            )
            .forEach((el) => {
                if (el === mainShape) return;

                const fill = el.style.fill || el.getAttribute("fill");
                const stroke = el.style.stroke || el.getAttribute("stroke");

                if (fill && fill !== "none") {
                    // White fills are background/hole elements (e.g. gear inner cutout);
                    // any other explicit fill is an icon shape (cross, envelope body, etc.).
                    const isBackground =
                        fill === "white" ||
                        fill === "#ffffff" ||
                        fill === "#fff";
                    el.style.fill = isBackground
                        ? "var(--p-content-background)"
                        : "var(--text-color)";
                    el.setAttribute(
                        "data-stekz-fill",
                        isBackground ? "background" : "icon"
                    );
                }
                if (!userStroke && stroke && stroke !== "none") {
                    el.style.stroke = "var(--text-color)";
                    el.setAttribute("data-stekz-stroke", "");
                }
            });
    }
}
